import { useState, useEffect, useRef } from "react";

const ARENA_W = 800, ARENA_H = 600, ARENA_R = 30;

const BUMPER_ROUND_NAMES = [
  { emoji: "🌿", name: "Открытое поле" },
  { emoji: "🧱", name: "Лабиринт" },
  { emoji: "🏝️", name: "Острова" },
  { emoji: "⛸️", name: "Ледяной каток" },
  { emoji: "👾", name: "Хаос" },
  { emoji: "🔴", name: "Красная зона" },
  { emoji: "💥", name: "Минное поле" },
  { emoji: "🌋", name: "Острова хаоса" },
];

// ── Canvas (30fps cap, static bg pre-rendered, no shadowBlur per frame) ─────
function BumperCanvas({ stateRef, arena }) {
  const canvasRef    = useRef(null);
  const bgCanvasRef  = useRef(null); // offscreen canvas for static background
  const arenaRef     = useRef(arena);
  const lastFrameRef = useRef(0);
  const ctxRef       = useRef(null);
  const fontRef      = useRef("");

  useEffect(() => { arenaRef.current = arena; }, [arena]);

  // Pre-render static background to offscreen canvas whenever arena changes
  useEffect(() => {
    if (!arena) return;
    const bg = document.createElement("canvas");
    bg.width  = ARENA_W;
    bg.height = ARENA_H;
    const bx = bg.getContext("2d");
    const W = ARENA_W, H = ARENA_H;
    const sx = 1, sy = 1;

    // Background fill
    bx.fillStyle = arena.bgColor || "#080618";
    bx.fillRect(0, 0, W, H);

    if (arena.isIslands && arena.platforms?.length) {
      const grad = bx.createRadialGradient(W/2, H/2, 0, W/2, H/2, W * 0.7);
      grad.addColorStop(0, "#00183a"); grad.addColorStop(1, "#000510");
      bx.fillStyle = grad; bx.fillRect(0, 0, W, H);
      bx.strokeStyle = "rgba(0,80,180,0.12)"; bx.lineWidth = 1;
      for (let gx = 0; gx < W; gx += 40) { bx.beginPath(); bx.moveTo(gx,0); bx.lineTo(gx,H); bx.stroke(); }
      for (let gy = 0; gy < H; gy += 40) { bx.beginPath(); bx.moveTo(0,gy); bx.lineTo(W,gy); bx.stroke(); }
      for (const p of arena.platforms) {
        bx.fillStyle = "#1a3a1a"; bx.fillRect(p.x, p.y, p.w, p.h);
        bx.fillStyle = "#2d7a2d"; bx.fillRect(p.x, p.y, p.w, 8);
        bx.strokeStyle = "#39FF14"; bx.lineWidth = 2.5;
        bx.strokeRect(p.x + 1, p.y + 1, p.w - 2, p.h - 2);
      }
    } else {
      // Dot grid — drawn once, not every frame
      bx.fillStyle = "rgba(155,93,229,0.10)";
      for (let gx = 50; gx < W; gx += 80)
        for (let gy = 50; gy < H; gy += 80) {
          bx.beginPath(); bx.arc(gx, gy, 2.5, 0, Math.PI * 2); bx.fill();
        }
    }

    if ((arena.bgColor || "").includes("2e")) {
      bx.fillStyle = "rgba(150,220,255,0.04)"; bx.fillRect(0, 0, W, H);
    }

    // Arena border — drawn once (no shadowBlur needed on static bg)
    bx.strokeStyle = "#9b5de5"; bx.lineWidth = 4;
    bx.strokeRect(2, 2, W - 4, H - 4);

    // Obstacles — drawn once, no per-frame texture loop
    for (const obs of (arena.obstacles || [])) {
      bx.fillStyle = "#3a1a00"; bx.fillRect(obs.x, obs.y, obs.w, obs.h);
      bx.strokeStyle = "#ff8c00"; bx.lineWidth = 2;
      bx.strokeRect(obs.x, obs.y, obs.w, obs.h);
    }

    bgCanvasRef.current = bg;
  }, [arena]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    ctxRef.current = canvas.getContext("2d");
    let animId;
    const FPS_CAP = 30;
    const FRAME_MS = 1000 / FPS_CAP;

    const draw = (ts) => {
      animId = requestAnimationFrame(draw);
      // Throttle to 30fps
      if (ts - lastFrameRef.current < FRAME_MS) return;
      lastFrameRef.current = ts;

      const { players = [] } = stateRef.current || {};
      const ar  = arenaRef.current || {};
      const ctx = ctxRef.current;
      const W = canvas.width, H = canvas.height;
      const sx = W / ARENA_W, sy = H / ARENA_H;
      const r  = ARENA_R * Math.min(sx, sy);

      // Draw pre-rendered background
      if (bgCanvasRef.current) {
        ctx.drawImage(bgCanvasRef.current, 0, 0, W, H);
      } else {
        ctx.fillStyle = ar.bgColor || "#080618";
        ctx.fillRect(0, 0, W, H);
      }

      // Cache font string (avoid string interpolation every frame)
      const fs = Math.max(10, r * 0.55);
      const fontStr = `bold ${Math.round(fs)}px Fredoka One, sans-serif`;
      if (fontRef.current !== fontStr) {
        fontRef.current = fontStr;
      }

      // Players
      for (const p of players) {
        const x = p.x * sx, y = p.y * sy;
        const healthFrac = Math.max(0, p.health / 100);
        const isBot = p.is_bot;

        if (!p.alive) {
          ctx.globalAlpha = 0.18;
          ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fillStyle = p.color; ctx.fill();
          ctx.globalAlpha = 1;
          continue;
        }

        // Trail — simple line, no gradient (much cheaper)
        const spd = Math.hypot(p.vx || 0, p.vy || 0);
        if (spd > 1.5) {
          const tLen = Math.min(spd * 4.5, 40) * Math.min(sx, sy);
          const ang  = Math.atan2(p.vy || 0, p.vx || 0) + Math.PI;
          ctx.beginPath();
          ctx.moveTo(x, y);
          ctx.lineTo(x + Math.cos(ang) * tLen, y + Math.sin(ang) * tLen);
          ctx.strokeStyle = p.color + "55"; // flat color instead of gradient
          ctx.lineWidth = r * 0.7; ctx.lineCap = "round"; ctx.stroke(); ctx.lineCap = "butt";
        }

        // Player circle — no shadowBlur (expensive), use solid ring instead
        ctx.beginPath(); ctx.arc(x, y, r + 3, 0, Math.PI * 2);
        ctx.fillStyle = isBot ? "#ff000044" : p.color + "44";
        ctx.fill();

        ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fillStyle = isBot ? "#220000" : p.color; ctx.fill();
        ctx.strokeStyle = "#000"; ctx.lineWidth = 2.5; ctx.stroke();

        if (isBot) {
          ctx.font = `${Math.round(r * 1.1)}px sans-serif`;
          ctx.textAlign = "center"; ctx.textBaseline = "middle";
          ctx.fillText("👾", x, y);
        } else {
          ctx.beginPath(); ctx.arc(x, y - r * 0.1, r * 0.42, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(0,0,0,0.5)"; ctx.fill();
        }

        // Health bar
        const bW = r * 2.9, bH = 5, bX = x - bW / 2, bY = y + r + 4;
        ctx.fillStyle = "#111"; ctx.fillRect(bX, bY, bW, bH);
        const hc = healthFrac > 0.5 ? "#3dffa0" : healthFrac > 0.25 ? "#ffe600" : "#ff3355";
        ctx.fillStyle = hc; ctx.fillRect(bX, bY, bW * healthFrac, bH);

        // Name
        ctx.font = fontRef.current;
        ctx.textAlign = "center"; ctx.textBaseline = "bottom";
        ctx.strokeStyle = "#000"; ctx.lineWidth = 3;
        ctx.strokeText(p.name, x, y - r - 3);
        ctx.fillStyle = isBot ? "#ff6666" : "#fff";
        ctx.fillText(p.name, x, y - r - 3);
      }
      ctx.textBaseline = "alphabetic";
    };

    animId = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animId);
  }, []); // eslint-disable-line

  return (
    <canvas ref={canvasRef} width={ARENA_W} height={ARENA_H} style={{
      display: "block",
      maxWidth: "100%",
      maxHeight: "calc(100vh - 110px)",
      width: "auto", height: "auto",
      borderRadius: "1rem", border: "2.5px solid #000", boxShadow: "6px 6px 0 #000",
    }} />
  );
}

// ── Host Screen ───────────────────────────────────────────────────────────────
export default function BumperHost({ stateRef, arena, players, elims, roundResult, tv }) {
  const [displayTime,   setDisplayTime]   = useState(60);
  const [displayScores, setDisplayScores] = useState({});
  const [liveAlive,     setLiveAlive]     = useState({});

  useEffect(() => {
    const iv = setInterval(() => {
      const s = stateRef.current;
      if (s) {
        setDisplayTime(s.timeLeft ?? 60);
        setDisplayScores(s.scores || {});
        const aliveMap = {};
        for (const p of (s.players || [])) aliveMap[p.name] = p.alive;
        setLiveAlive(aliveMap);
      }
    }, 250);
    return () => clearInterval(iv);
  }, [stateRef]);

  const timerColor = displayTime > 30 ? "#3dffa0" : displayTime > 10 ? "#ffe600" : "#ff3355";

  // Between-rounds intermission
  if (roundResult && roundResult.round_num < roundResult.total_rounds) {
    const nextRound = BUMPER_ROUND_NAMES[roundResult.round_num];
    const sorted    = Object.entries(displayScores).sort((a, b) => b[1] - a[1]);
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-6 animate-slide-up">
        <div style={{ fontSize: tv ? "4rem" : "2.5rem" }}>🏁</div>
        <h2 className="font-display text-white" style={{ fontSize: tv ? "2.5rem" : "1.8rem" }}>
          Раунд {roundResult.round_num} завершён!
        </h2>
        {roundResult.round_winner ? (
          <div className="card-cartoon text-center" style={{ padding: "1rem 2.5rem" }}>
            <p className="font-heading text-[#aaa]">Победитель раунда</p>
            <p className="font-display" style={{ color: "#ffe600", fontSize: tv ? "2rem" : "1.4rem" }}>
              🏆 {roundResult.round_winner}
            </p>
          </div>
        ) : (
          <div className="card-cartoon text-center" style={{ padding: "1rem 2rem" }}>
            <p className="font-display text-[#aaa]">Ничья!</p>
          </div>
        )}
        {nextRound && (
          <p className="font-heading text-[#aaa]" style={{ fontSize: tv ? "1.1rem" : "0.85rem" }}>
            Следующий раунд: {nextRound.emoji} {nextRound.name}
          </p>
        )}
        <div className="flex flex-col gap-2 w-48">
          {sorted.map(([name, score], i) => (
            <div key={name} className="flex justify-between font-heading" style={{
              color: i === 0 ? "#ffe600" : "#fff", fontSize: tv ? "1.1rem" : "0.9rem",
            }}>
              <span>{i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : "  "} {name}</span>
              <span>{score}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full flex ${tv ? "flex-row gap-4" : "flex-col gap-2"} animate-slide-up`}
      style={{ minHeight: 0, flex: 1, overflow: "hidden" }}>

      {/* Canvas area */}
      <div style={{
        flex: tv ? "1 1 0" : "unset",
        minHeight: 0, minWidth: 0,
        maxWidth: tv ? "75%" : "100%",
        position: "relative",
      }}>
        <BumperCanvas stateRef={stateRef} arena={arena} />

        {/* Round badge */}
        {arena && (
          <div style={{
            position: "absolute", top: "0.5rem", left: "0.6rem",
            background: "rgba(0,0,0,0.75)", border: "2px solid #9b5de5",
            borderRadius: "0.5rem", padding: "0.2rem 0.7rem",
            color: "#9b5de5", fontSize: tv ? "1rem" : "0.75rem",
            fontFamily: "'Fredoka One',sans-serif", pointerEvents: "none",
          }}>
            {arena.roundEmoji} {arena.roundNum}/{arena.totalRounds} — {arena.roundName}
          </div>
        )}

        {/* Timer */}
        <div className="font-display" style={{
          position: "absolute", top: "0.5rem", left: "50%", transform: "translateX(-50%)",
          background: "rgba(0,0,0,0.75)", border: `2.5px solid ${timerColor}`,
          borderRadius: "0.75rem", padding: tv ? "0.35rem 1.4rem" : "0.2rem 0.9rem",
          color: timerColor, fontSize: tv ? "2rem" : "1.4rem",
          textShadow: `0 0 12px ${timerColor}`,
          animation: displayTime <= 10 ? "wiggle .4s ease-in-out infinite" : "none",
          pointerEvents: "none",
        }}>⏱ {displayTime}</div>
      </div>

      {/* Scoreboard */}
      <div className={tv ? "w-48 flex flex-col gap-3" : "flex flex-row gap-2 flex-wrap justify-center"}>
        {players.slice().sort((a, b) => (displayScores[b.name] || 0) - (displayScores[a.name] || 0)).map((p, i) => {
          const alive = liveAlive[p.name] !== false;
          return (
            <div key={i} className="player-chip" style={{
              backgroundColor: p.color, opacity: alive ? 1 : 0.35,
              fontSize: tv ? "1rem" : "0.8rem",
              padding: tv ? "0.45rem 1rem" : "0.3rem 0.6rem",
            }}>
              {!alive && <span style={{ marginRight: "0.3rem" }}>💀</span>}
              <span>{p.name}</span>
              <span className="ml-2 font-display" style={{ fontSize: tv ? "1.1rem" : "0.85rem" }}>
                {displayScores[p.name] || 0}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
